import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatestWith, Observable, Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Location } from '../../../models/locations-models/location';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { IntersectionStatus } from '../../../directives/general-intersection/from-intersection-observer';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { EditLocationSection } from './edit-location.component';
import { Store } from '@ngrx/store';
import { FormGroup } from '@angular/forms';
import { EditLocationService } from './service/edit-location.service';
import { SkeletonRightPanelActions } from '../state/locations-skeleton-actions';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import { EditLocationActions } from '../location-list/state/location-list-actions';
import { EditLocationTControl } from '../../../models/locations-models/edit-location-form';

export interface SectionVisibility {
  section: EditLocationSection;
  visibility: IntersectionStatus;
}

export interface EditLocationState {
  initialLocation: Location | undefined;
  loading: boolean;
  saving: boolean;
  edited: boolean;
  sectionVisibilities: EntityState<SectionVisibility>;
}

export const sectionVisibilityAdapter = createEntityAdapter<SectionVisibility>({
  selectId: (sectionVisibility) => sectionVisibility.section,
});

@Injectable()
export class EditLocationLocalStore extends ComponentStore<EditLocationState> {

  formGroup: FormGroup<EditLocationTControl> | undefined;
  private newFormGroupNotifier = new Subject<void>();

  constructor(private globalActions$: Actions, private store: Store, private editLocationService: EditLocationService) {

    super({
      initialLocation: undefined,
      loading: true,
      saving: false,
      edited: false,
      sectionVisibilities: sectionVisibilityAdapter.setAll([
            { section: EditLocationSection.Basic, visibility: IntersectionStatus.NotVisible },
            { section: EditLocationSection.Hours, visibility: IntersectionStatus.NotVisible },
            { section: EditLocationSection.Location, visibility: IntersectionStatus.NotVisible },
            { section: EditLocationSection.Media, visibility: IntersectionStatus.NotVisible },
            { section: EditLocationSection.Other, visibility: IntersectionStatus.NotVisible },
          ],
          sectionVisibilityAdapter.getInitialState()),
    });

    this.initializeLocation(this.store.select(locationListSelectors.selectFocusedLocation));

    this.stopLoadingAfterBLSUpdate(globalActions$.pipe(
        ofType(EditLocationActions.editLocationDataSuccess, EditLocationActions.editLocationDataError),
        map((action) => action.location)));
  }

  // Selectors

  readonly initialLocation$: Observable<Location> = this.select(state => state.initialLocation);

  readonly loading$: Observable<boolean> = this.select(state => state.loading);

  readonly saving$: Observable<boolean> = this.select(state => state.saving);

  readonly edited$: Observable<boolean> = this.select(state => state.edited);

  readonly sectionVisibilities$ = sectionVisibilityAdapter.getSelectors().selectAll;

  readonly uppermostVisibleSection$: Observable<EditLocationSection> = this.select(state => {
    const uppermostVisibleSection = this.sectionVisibilities$(state.sectionVisibilities)
        .find(sectionVisibility => sectionVisibility.visibility === IntersectionStatus.Visible);
    return uppermostVisibleSection ? uppermostVisibleSection.section : EditLocationSection.Basic;
  });

  readonly newFormGroupNotifier$: Observable<void> = this.newFormGroupNotifier.asObservable();

  // Updaters

  readonly updateInitialLocation = this.updater((state, initialLocation: Location): EditLocationState => (
      { ...state, initialLocation }
  ));

  readonly updateLoading = this.updater((state, loading: boolean): EditLocationState => (
      { ...state, loading }
  ));

  readonly updateSaving = this.updater((state, saving: boolean): EditLocationState => (
      { ...state, saving }
  ));

  readonly updateEdited = this.updater((state, edited: boolean): EditLocationState => {
    this.store.dispatch(SkeletonRightPanelActions.setUnsavedChangesInRightPanelTab({ unsavedChangesInRightPanelTab: edited }));
    return ({ ...state, edited });
  });

  readonly updateSectionVisibility = this.updater((state, sectionVisibility: SectionVisibility): EditLocationState => (
      {
        ...state,
        sectionVisibilities: sectionVisibilityAdapter.setOne(sectionVisibility, state.sectionVisibilities),
      }
  ));

  // Effects

  readonly stopLoadingAfterBLSUpdate = this.effect((updatedLocation$: Observable<Location>) => {
    return updatedLocation$.pipe(
        combineLatestWith(this.initialLocation$),
        filter(([updatedLocation, initialLocation]) => updatedLocation.id === initialLocation.id),
        tap(() => {
              this.updateSaving(false);
              this.updateEdited(false);
              this.updateInitialLocation(updatedLocation$);
            },
        ));
  });

  readonly initializeLocation = this.effect((focusedLocation$: Observable<Location>) => {
    return focusedLocation$.pipe(
        concatLatestFrom(() => this.state$),
        tap(([focusedLocation, state]) => {
          // listen & react to newly focused locations
          // when no location is focused -> clear form
          if (!focusedLocation) {
            this.formGroup = undefined;
            this.newFormGroupNotifier.next();
            this.updateInitialLocation(focusedLocation);
            return;
          }

          // Will occur when the user saves the current changes or when the user is prompted to
          // review their changes and the previous row automatically gets focused again.
          if (focusedLocation.id === state.initialLocation?.id && this.formGroup) {
            this.updateInitialLocation(focusedLocation);
            return; // keep already focused location
          }

          this.updateLoading(true);
          this.updateInitialLocation(focusedLocation);

          // expensive operation; 200ms is arbitrary but works well
          setTimeout(() => {
            this.initializeFormGroup();
          }, 200);
        }));
  });

  readonly initializeFormGroup = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
        concatLatestFrom(() => this.state$),
        tap(([_, state]) => {
          (this.formGroup as FormGroup) = new FormGroup({});
          // init listener that checks if location has been edited
          // using distinctUntilChanged because the operation is expensive
          this.initializeFormGroupValueListener(
              this.formGroup?.valueChanges.pipe(
                  distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
              ),
          );

          // determine which fields are editable and create a form control for each
          this.editLocationService.addFormControls(state.initialLocation, this.formGroup);
          // show immediately if fields are invalid
          this.formGroup?.markAllAsTouched();
          this.newFormGroupNotifier.next();
          this.updateLoading(false);
          this.updateSaving(false);
        }));
  });

  readonly initializeFormGroupValueListener = this.effect((trigger$: Observable<any>) => {
    return trigger$.pipe(
        concatLatestFrom(() => this.state$),
        tap(([_, state]) => {
          const edited = this.formGroup?.touched && this.editLocationService
              .areEditableValuesDifferent(state.initialLocation, this.editedLocation);
          if (edited !== state.edited) {
            this.updateEdited(edited);
          }
        }));
  });

  // Utils

  private get editedLocation(): Location {
    return this.formGroup ? this.editLocationService.formGroupToLocation(this.formGroup) : undefined;
  }

}
